import { z } from 'zod'
import type { Tool } from '../types.js'

export interface RememberDeps {
  /** Append a fact to the agent's durable memory. Implementation lives in the runtime. */
  writeFact: (agentId: string, fact: string) => Promise<void>
  /** Remove a fact by its index (0-based) from the agent's durable memory. */
  removeFact: (agentId: string, factIndex: number) => Promise<void>
}

const rememberSchema = z.object({
  fact: z.string().min(1).describe('A short, durable fact to remember about the user or entity.'),
})

const forgetSchema = z.object({
  factIndex: z.number().int().nonnegative().describe('The 0-based index of the fact to forget.'),
})

export const createRememberTool = (deps: RememberDeps): Tool<z.infer<typeof rememberSchema>, { saved: true }> => ({
  name: 'remember',
  description:
    'Save a short durable fact about the user or this entity. Use sparingly, only for facts that should persist across sessions (preferences, long-running goals).',
  schema: rememberSchema,
  handler: async (args, ctx) => {
    await deps.writeFact(ctx.agentId, args.fact)
    return { ok: true, result: { saved: true } }
  },
})

export const createForgetTool = (deps: RememberDeps): Tool<z.infer<typeof forgetSchema>, { removed: true }> => ({
  name: 'forget',
  description: 'Remove a previously-saved fact by its index, shown in the agent memory section.',
  schema: forgetSchema,
  handler: async (args, ctx) => {
    await deps.removeFact(ctx.agentId, args.factIndex)
    return { ok: true, result: { removed: true } }
  },
})
